import { IModel, IMyModel } from 'types/Model.types';
import {
  AbsoluteCircleWrapper,
  ButtonWrapper,
  CategoryLabel,
  Container,
  FavoriteWrapper,
  ModelIdLabel,
  NameLabel,
  Reference,
  RowCenter,
  RowSection,
  favoriteIconStyle,
  imageStyle,
} from './ModelCard.styled';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCustomNetPrice,
  setModel,
  setPricing,
  setQuestions,
} from 'store/Model/actions/model';
import { useTranslation } from 'react-i18next';
import { IRootReducerState } from 'store/store';
import { setCurrentlyEditingShoppingCartLineData } from 'store/ShoppingCart/actions/shoppingCart';
import { setIndicativeQuestionKeys } from 'store/SubmitConfiguration/actions/submitConfiguration';
import { AppMode } from 'store/Common/reducers/common';
import {
  AdaptiveImage,
  AdaptiveImageType,
} from 'components/AdaptiveImage/AdaptiveImage';
import {
  resetManuallyChangedQuestionNames,
  resetManuallyClearedQuestionNames,
  resetTouchedQuestionNames,
  setAreAllFieldsTouched,
} from 'store/Form/actions/form';
import { FavoriteButton } from './FavoriteButton/FavoriteButton';
import Button from 'components/Button/Button';
import { setOrderProductsStep, setProductId } from 'store/Order/actions/order';
import { OrderProductsSteps } from 'types/Order.types';
import { getLogoNavigationDestinationBasedOnAppMode } from 'components/Layout/Header/helpers';
import Tooltip from 'components/Tooltip/Tooltip';

interface IModelCardProps {
  model: IModel | IMyModel;
  onToggleFavoriteSuccess?: () => void;
  onOpenModel?: (modelId: number) => void;
  myModels?: boolean;
  buttonLabel?: string;
}

const ModelCard = ({
  model,
  onToggleFavoriteSuccess,
  onOpenModel,
  myModels = false, // if true, model is IMyModel for MyModelsPage, otherwise it is IModel
  buttonLabel = 'Configure',
}: IModelCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const modelName =
    (myModels ? (model as IMyModel).name : (model as IModel).general.name) ||
    t('Unnamed model');

  const handleOnClick = () => {
    if (appMode === AppMode.SUPPLIER || appMode === AppMode.BUYER) {
      if (myModels) {
        onOpenModel && onOpenModel(model.id);
        return;
      }
      dispatch(setOrderProductsStep(OrderProductsSteps.Configuration));
      dispatch(setProductId(model.id.toString()));
      dispatch(resetTouchedQuestionNames());
      dispatch(resetManuallyChangedQuestionNames());
      dispatch(resetManuallyClearedQuestionNames());
      dispatch(setAreAllFieldsTouched(false));
      dispatch(setCurrentlyEditingShoppingCartLineData(null, null));
      dispatch(setIndicativeQuestionKeys([]));
      dispatch(setModel({ id: model.id }));
      dispatch(setQuestions({}));
      dispatch(setPricing({}));
      dispatch(setCustomNetPrice(undefined));
    } else {
      navigate(getLogoNavigationDestinationBasedOnAppMode(appMode));
    }
  };

  return (
    <Container data-testid={`model-card-${model.id}`}>
      <AbsoluteCircleWrapper>
        <ModelIdLabel>{model.id}</ModelIdLabel>
      </AbsoluteCircleWrapper>

      <AdaptiveImage
        imageUrl={
          myModels
            ? (model as IMyModel).image
            : (model as IModel).general.images[0]
        }
        type={AdaptiveImageType.MODEL}
        imageStyle={imageStyle}
      />
      <RowCenter>
        <RowSection>
          <NameLabel>
            <Tooltip content={modelName.length > 20 ? modelName : undefined}>
              {modelName}
            </Tooltip>
          </NameLabel>

          <FavoriteWrapper>
            {onToggleFavoriteSuccess && (
              <FavoriteButton
                key={`${model.id}${(model as IModel).is_favorite}`}
                favoriteIconStyle={favoriteIconStyle}
                modelId={model.id}
                initialIsFavorite={(model as IModel).is_favorite}
                onToggleSuccess={onToggleFavoriteSuccess}
              />
            )}
          </FavoriteWrapper>
        </RowSection>
        <RowSection>
          <CategoryLabel>
            {myModels
              ? (model as IMyModel).category_name
              : (model as IModel).category?.name || ''}
          </CategoryLabel>
          <Reference>
            #
            {myModels
              ? (model as IMyModel).last_version_id
              : (model as IModel).version_number}
          </Reference>
        </RowSection>
        <ButtonWrapper>
          <Button
            primary
            label={t(buttonLabel)}
            width="100%"
            height="25rem"
            wrapperStyle={{ width: '100%' }}
            onClick={handleOnClick}
          />
        </ButtonWrapper>
      </RowCenter>
    </Container>
  );
};
export default ModelCard;
